import type { FC, ReactNode } from 'react'
import type { JobFacets } from '@/lib/jobs/queries'
import type { JobFilters } from '@/lib/jobs/filters'
import type { Facet } from '@/types'

import { useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { SimpleSelect } from '@/components/ui/select'
import { Button } from '@/components/ui/button'

import { POSTED_WITHIN, SALARY_FLOORS } from '@/constants/taxonomy'

interface FilterGroupProps {
    title: string
    children: ReactNode
}

const FilterGroup: FC<FilterGroupProps> = ({ title, children }) => (
    <fieldset className='flex flex-col gap-2.5 border-b border-border/70 pb-5 last:border-b-0'>
        <legend className='mb-2.5 text-sm font-semibold text-foreground'>{title}</legend>
        {children}
    </fieldset>
)

interface CheckListProps {
    name: string
    facets: Array<Facet>
    selected: Array<string>
    onToggle: (value: string) => void
    limit?: number
}

const CheckList: FC<CheckListProps> = ({ name, facets, selected, onToggle, limit = 8 }) => {
    const [expanded, setExpanded] = useState(false)
    const visible = expanded ? facets : facets.slice(0, limit)

    return (
        <div className='flex flex-col gap-2'>
            {visible.map(facet => {
                const checked = selected.includes(facet.value)
                return (
                    <label key={`${name}-${facet.value}`} className='flex cursor-pointer items-center gap-2.5 text-sm'>
                        <Checkbox checked={checked} onCheckedChange={() => onToggle(facet.value)} />
                        <span className={cn('flex-1 truncate', checked ? 'font-medium text-foreground' : 'text-foreground/80')}>{facet.label}</span>
                        <span className='text-xs text-muted-foreground tabular-nums'>{facet.count}</span>
                    </label>
                )
            })}
            {facets.length > limit && (
                <button
                    type='button'
                    onClick={() => setExpanded(value => !value)}
                    className='self-start text-xs font-semibold text-primary hover:underline'
                >
                    {expanded ? 'Show fewer' : `Show all ${facets.length}`}
                </button>
            )}
        </div>
    )
}

interface JobFiltersPanelProps {
    filters: JobFilters
    facets: JobFacets
    onChange: (next: Partial<JobFilters>) => void
    onClear: () => void
}

const toggle = <T extends string>(list: Array<T>, value: T): Array<T> =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value]

export const JobFiltersPanel: FC<JobFiltersPanelProps> = ({ filters, facets, onChange, onClear }) => (
    <div className='flex flex-col gap-5'>
        <div className='flex items-center justify-between'>
            <h2 className='text-base font-semibold'>Filters</h2>
            <Button variant='link' size='sm' className='h-auto px-0' onClick={onClear}>
                Clear all
            </Button>
        </div>

        {filters.skill && (
            <FilterGroup title='Skill'>
                <button
                    type='button'
                    onClick={() => onChange({ skill: '' })}
                    className='inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary'
                >
                    {filters.skill}
                    <X className='size-3.5' />
                    <span className='sr-only'>Remove skill filter</span>
                </button>
            </FilterGroup>
        )}

        <FilterGroup title='Work type'>
            <CheckList
                name='work'
                facets={facets.workTypes}
                selected={filters.workTypes}
                onToggle={value => onChange({ workTypes: toggle(filters.workTypes, value as JobFilters['workTypes'][number]) })}
            />
        </FilterGroup>

        <FilterGroup title='Experience'>
            <CheckList
                name='level'
                facets={facets.levels}
                selected={filters.levels}
                onToggle={value => onChange({ levels: toggle(filters.levels, value as JobFilters['levels'][number]) })}
            />
        </FilterGroup>

        <FilterGroup title='Role category'>
            <CheckList
                name='category'
                facets={facets.categories}
                selected={filters.categories}
                onToggle={value => onChange({ categories: toggle(filters.categories, value as JobFilters['categories'][number]) })}
            />
        </FilterGroup>

        <FilterGroup title='Salary'>
            <SimpleSelect
                aria-label='Minimum salary'
                value={filters.salaryMin ? String(filters.salaryMin) : 'any'}
                onValueChange={value => onChange({ salaryMin: value === 'any' ? null : Number(value) })}
                options={[{ value: 'any', label: 'Any salary' }, ...SALARY_FLOORS]}
            />
            <p className='text-xs text-muted-foreground'>Based on listings that publish a US dollar range.</p>
        </FilterGroup>

        <FilterGroup title='Date posted'>
            <SimpleSelect
                aria-label='Date posted'
                value={filters.postedWithin ? String(filters.postedWithin) : 'any'}
                onValueChange={value => onChange({ postedWithin: value === 'any' ? null : Number(value) })}
                options={[{ value: 'any', label: 'Any time' }, ...POSTED_WITHIN]}
            />
        </FilterGroup>

        {facets.companies.length > 0 && (
            <FilterGroup title='Company'>
                <CheckList
                    name='company'
                    facets={facets.companies}
                    selected={filters.companies}
                    onToggle={value => onChange({ companies: toggle(filters.companies, value) })}
                    limit={6}
                />
            </FilterGroup>
        )}
    </div>
)
